import { createMutation, createTriggerResource, toast, useLocalization } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { surveyRepository } from '~/repositories/surveyRepository';
import type { SurveyListFilter } from '~/repositories/surveyRepository';
import type { CreateSurveyDto, UpdateSurveyDto } from '~/swagger/hoa/Api';

type UpdateSurveyRequest = {
  id: string;
  payload: UpdateSurveyDto;
};

export const [SurveysProvider, useSurveys] = createMagicDoorContext('Surveys', () => {
  const { t } = useLocalization();
  const [filtered, setFilter, filteredActions] = createTriggerResource((filter: SurveyListFilter) => surveyRepository.list(filter));
  const [selected, getDetail, selectedActions] = createTriggerResource((id: string) => surveyRepository.get(id));
  const [results, getResults, resultsActions] = createTriggerResource((id: string) => surveyRepository.getResults(id));

  const refetchAll = () => {
    filteredActions.refetch();
    selectedActions.refetch();
    resultsActions.refetch();
  };

  const runMutation = async <T,>(runner: () => Promise<T>, successKey: string) => {
    const response = await runner();
    refetchAll();
    toast(t(successKey), 'success');
    return response;
  };

  const create = createMutation((payload: CreateSurveyDto) =>
    runMutation(() => surveyRepository.create(payload), 'Survey created successfully')
  );
  const update = createMutation((request: UpdateSurveyRequest) =>
    runMutation(() => surveyRepository.update(request.id, request.payload), 'Survey updated successfully')
  );
  const publish = createMutation((id: string) => runMutation(() => surveyRepository.publish(id), 'Survey published successfully'));
  const close = createMutation((id: string) => runMutation(() => surveyRepository.close(id), 'Survey closed successfully'));

  return {
    filtered,
    setFilter,
    filteredActions,
    selected,
    getDetail,
    selectedActions,
    results,
    getResults,
    resultsActions,
    create,
    update,
    publish,
    close,
  };
});
